// libs
import { useEffect, useRef, useState } from "react";

// internal-deps
import { isEmpty, isPlainObject } from "@swiggy-private/js-utils";
import { useMountedRef } from "@swiggy-private/core-ui";
import {
    AmazonSdkGetBalanceResponseData,
    AmazonSdkAvailableResponseData,
    AmazonPayWasPreviouslyLinkedResponseData,
} from "@swiggy-private/native-bridge";
import { VALID_WALLET_TYPES } from "@swiggy-private/payment-core";
import { useSessionStorage, getBridgeSdk } from "@swiggy-private/checkout-core";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { AmazonPaySdkState } from "@swiggy-private/payment-ui";

// contexts
import { AmazonPaySdkContextValue } from "@payment-client/contexts/AmazonPaySdkContext";

// helpers
import { recordNativeBridgeCall, recordStateInvalidation } from "@payment-client/helpers/AmazonPayNREvents";
import {
    getAmazonPayStateStaleReason,
    getAmazonPayDerivedState,
    buildAplState,
} from "@payment-client/helpers/AmazonPaySdkHelper";

// types
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";

// constants
import { AMAZON_PAY_NB_STATES, APAY_NB_CALL_STATUS } from "@payment-client/constants/AmazonPay";

const useAmazonPaySdkAvailable = (
    paymentResponse: GetPaymentMethodsResponse,
    amountToPay: number,
    isTurboFlow: boolean,
): AmazonPaySdkContextValue => {
    const isMountedRef = useMountedRef();
    const [state, setState] = useSessionStorage<AmazonPaySdkState | null>("apay_sdk", null);
    const [availableCallInProgress, setAvailableCallInProgress] = useState(false);
    const {
        isAPayMethodAvailable,
        isAmazonPayLaterAvailable,
        isNewApaySDKAvailable,
        isApbLinked,
        isAplLinked,
        isAnyAmazonPayLinked,
        apayBalance,
        aplCreditLimit,
        aplKeyFactStatementUrl,
    } = getAmazonPayDerivedState(paymentResponse);
    const tenant = paymentResponse.tenant;
    const lastReconciledResponseRef = useRef<GetPaymentMethodsResponse | null>(null);

    useEffect(() => {
        if (!isPlainObject(paymentResponse) || availableCallInProgress) {
            return;
        }

        if (!isAPayMethodAvailable && !isAmazonPayLaterAvailable) {
            return;
        }

        if (state != null) {
            if (!isNewApaySDKAvailable) {
                return;
            }

            const hasNewPaymentResponse = lastReconciledResponseRef.current !== paymentResponse;
            lastReconciledResponseRef.current = paymentResponse;

            const staleReason = getAmazonPayStateStaleReason({
                state,
                hasNewPaymentResponse,
                isApbLinked,
                isAplLinked,
                apayBalance,
                isAPayMethodAvailable,
                isAmazonPayLaterAvailable,
                amountToPay,
                aplCreditLimit,
            });

            if (staleReason) {
                recordStateInvalidation(staleReason);
                setState(null);
            }
            return;
        }

        lastReconciledResponseRef.current = paymentResponse;

        if (isMountedRef.current) {
            setAvailableCallInProgress(true);
        }
        const requestPayload = {
            state: AMAZON_PAY_NB_STATES.IS_SDK_AVAILABLE,
            ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
        };

        const sdk = getBridgeSdk();

        const aplStateFromGpo = isAmazonPayLaterAvailable
            ? buildAplState(!!isAplLinked, isAplLinked ? aplCreditLimit : undefined, amountToPay, {
                  keyFactStatementUrl: isAplLinked ? aplKeyFactStatementUrl : undefined,
              })
            : undefined;

        const handleNewSdkAvailabilityFlow = async () => {
            if (!isApbLinked) {
                const wasPreviouslyLinkedPayload = {
                    state: AMAZON_PAY_NB_STATES.WAS_PREVIOUSLY_LINKED,
                    ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
                };

                try {
                    const nbStart = Date.now();
                    const wasLinkedRes = (await sdk.payUsingAmazonPay(
                        wasPreviouslyLinkedPayload,
                        2 * 1000,
                    )) as AmazonPayWasPreviouslyLinkedResponseData;
                    const wasLinked = wasLinkedRes && wasLinkedRes.wasPreviouslyLinked;

                    recordNativeBridgeCall({
                        nbState: AMAZON_PAY_NB_STATES.WAS_PREVIOUSLY_LINKED,
                        isNewSdk: true,
                        status: APAY_NB_CALL_STATUS.SUCCESS,
                        latencyMs: Date.now() - nbStart,
                        extra: { wasPreviouslyLinked: String(!!wasLinked) },
                    });

                    if (isMountedRef.current) {
                        setState({
                            available: true,
                            linked: false,
                            wasPreviouslyLinkedUsingSDK: wasLinked,
                            isBalanceSufficient: false,
                            isNewSdkAvailable: true,
                            apl: aplStateFromGpo,
                        });
                        setAvailableCallInProgress(false);
                    }
                } catch {
                    recordNativeBridgeCall({
                        nbState: AMAZON_PAY_NB_STATES.WAS_PREVIOUSLY_LINKED,
                        isNewSdk: true,
                        status: APAY_NB_CALL_STATUS.FAILURE,
                    });

                    if (isMountedRef.current) {
                        setState({
                            available: true,
                            linked: false,
                            isBalanceSufficient: false,
                            isNewSdkAvailable: true,
                            apl: aplStateFromGpo,
                        });
                        setAvailableCallInProgress(false);
                    }
                }
                return;
            }

            if (isMountedRef.current) {
                setState({
                    available: true,
                    linked: true,
                    balance: apayBalance,
                    isBalanceSufficient: Number(apayBalance) >= amountToPay,
                    isNewSdkAvailable: true,
                    apl: aplStateFromGpo,
                });
                setAvailableCallInProgress(false);
            }
        };

        const sdkCheckStart = Date.now();
        sdk.payUsingAmazonPay(requestPayload, 2 * 1000)
            .catch(() => {
                recordNativeBridgeCall({
                    nbState: AMAZON_PAY_NB_STATES.IS_SDK_AVAILABLE,
                    isNewSdk: !!isNewApaySDKAvailable,
                    status: APAY_NB_CALL_STATUS.FAILURE,
                    latencyMs: Date.now() - sdkCheckStart,
                });
                setAvailableCallInProgress(false);
                return {
                    available: false,
                    linked: false,
                    isBalanceSufficient: false,
                    isNewSdkAvailable: isNewApaySDKAvailable,
                    apl: isAmazonPayLaterAvailable ? { linked: false, isCreditLimitSufficient: false } : undefined,
                };
            })
            .then((resp) => {
                if (isMountedRef.current) {
                    const response = resp as AmazonSdkAvailableResponseData;
                    recordNativeBridgeCall({
                        nbState: AMAZON_PAY_NB_STATES.IS_SDK_AVAILABLE,
                        isNewSdk: !!isNewApaySDKAvailable,
                        status: APAY_NB_CALL_STATUS.SUCCESS,
                        latencyMs: Date.now() - sdkCheckStart,
                        extra: { isAmazonSdkAvailable: String(!!response?.isAmazonSdkAvailable) },
                    });

                    if (!response || !response.isAmazonSdkAvailable) {
                        setState({
                            available: false,
                            linked: false,
                            isBalanceSufficient: false,
                            isNewSdkAvailable: isNewApaySDKAvailable,
                            apl: isAmazonPayLaterAvailable
                                ? { linked: false, isCreditLimitSufficient: false }
                                : undefined,
                        });
                        setAvailableCallInProgress(false);
                        return;
                    }

                    if (isNewApaySDKAvailable) {
                        handleNewSdkAvailabilityFlow();
                    } else {
                        const getBalanceRequestPayload = {
                            state: AMAZON_PAY_NB_STATES.GET_BALANCE,
                            data: {
                                methodName: VALID_WALLET_TYPES.AMAZONPAY,
                            },
                            ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
                        };

                        const getBalanceStart = Date.now();
                        sdk.payUsingAmazonPay(getBalanceRequestPayload, 20 * 1000)
                            .catch(() => {
                                recordNativeBridgeCall({
                                    nbState: AMAZON_PAY_NB_STATES.GET_BALANCE,
                                    isNewSdk: false,
                                    status: APAY_NB_CALL_STATUS.FAILURE,
                                    latencyMs: Date.now() - getBalanceStart,
                                });
                                return { available: true, linked: false, isBalanceSufficient: false };
                            })
                            .then((resp) => {
                                if (isMountedRef.current) {
                                    const response = resp as AmazonSdkGetBalanceResponseData;
                                    let isBalanceSufficient = false;
                                    if (response && response.linked && response.balance) {
                                        isBalanceSufficient = Number(response.balance) >= amountToPay;
                                    }
                                    recordNativeBridgeCall({
                                        nbState: AMAZON_PAY_NB_STATES.GET_BALANCE,
                                        isNewSdk: false,
                                        status: APAY_NB_CALL_STATUS.SUCCESS,
                                        latencyMs: Date.now() - getBalanceStart,
                                        extra: {
                                            linked: String(!!response?.linked),
                                            isBalanceSufficient: String(isBalanceSufficient),
                                        },
                                    });
                                    setState({
                                        ...resp,
                                        isBalanceSufficient,
                                        available: true,
                                    } as AmazonPaySdkContextValue["state"]);
                                    setAvailableCallInProgress(false);
                                }
                            });
                    }
                }
            });
    }, [
        isMountedRef,
        state,
        setState,
        availableCallInProgress,
        isAPayMethodAvailable,
        amountToPay,
        paymentResponse,
        isNewApaySDKAvailable,
        isApbLinked,
        isAplLinked,
        isAnyAmazonPayLinked,
        apayBalance,
        isTurboFlow,
        isAmazonPayLaterAvailable,
        aplCreditLimit,
        aplKeyFactStatementUrl,
    ]);

    return {
        state,
        setState,
        sdkFetchInProgress: availableCallInProgress,
        setSdkFetchInProgress: setAvailableCallInProgress,
    };
};

export default useAmazonPaySdkAvailable;
